/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { Link } from '@fluentui/react/lib/Link';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { ImportModal } from 'src/core/components/ImportModal';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logButtonClickWithCustomData, logQnaKBButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { QnaMakerAddQnaPairModal } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerAddQnaPairModal';
import { QnaMakerFiltersBlade } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerFiltersBlade';
import { QnaMakerQnaCard } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaCard';
import { QnaMakerQnaPairCommandBar } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairCommandBar';
import { QnaMakerFilterPillContainer } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFilterPillsContainer';
import { QnaMakerFiltersType, QuestionsBeingEditedFilterItem } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFiltersStore';
import {
    applyMetadataFilter,
    applyQuestionFilter,
    applySourcesFilter,
    getFlattenMetadata
} from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { DeletionOperations, QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerTestPane } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerTestPane';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { useQnaPairsActions } from 'src/qnaMaker/modules/hooks/useQnaPairsActions';

type QnaMakerQnaListProps = {
    qnaUiStore: QnaMakerQnaPairsUiStore;
    isCommandBarInExpandedState: boolean;
    setQnasCount: (count: number) => void;
    setMultiSelectMode: (state: boolean) => void;
};

const messages = defineMessages({
    emptyStateMessage: {
        id: 'qnaMaker.qnaMakerQnaList.emptyStateMessage',
        defaultMessage: 'No QnA pairs. Select add to create a new QnA pair'
    },
    loadingKnowledgeBaseFailedTitle: {
        id: 'qnaMaker.qnaMakerQnaList.loadingKnowledgeBaseFailedTitle',
        defaultMessage: 'Failed to load QnA pairs'
    },
    checkbox: {
        id: 'qnaMaker.qnaMakerQnaList.checkbox',
        defaultMessage: 'Checkbox'
    },
    deleteQnaPairModalTitle: {
        id: 'qnaMaker.qnaMakerQnaList.deleteQnaPairModalTitle',
        defaultMessage: 'Delete the selected question answer pair(s)'
    },
    deleteQnaPairModalBody: {
        id: 'qnaMaker.qnaMakerQnaList.deleteQnaPairModalBody',
        defaultMessage: 'Are you sure you want to delete the selected {selectedCount} pair(s)?'
    },
    deleteQnaMetadataModalTitle: {
        id: 'qnaMaker.qnaMakerQnaList.deleteQnaMetadataModalTitle',
        defaultMessage: 'Delete metadata of the selected question answer pair(s)'
    },
    deleteQnaMetadataModalBody: {
        id: 'qnaMaker.qnaMakerQnaList.deleteQnaMetadataModalBody',
        defaultMessage: 'Are you sure you want to delete the metadata of the selected pair(s)?'
    },
    deleteQnaPromptsModalTitle: {
        id: 'qnaMaker.qnaMakerQnaList.deleteQnaPromptsModalTitle',
        defaultMessage: 'Delete follow-up prompts of the selected question answer pair(s)'
    },
    deleteQnaPromptsModalBody: {
        id: 'qnaMaker.qnaMakerQnaList.deleteQnaPromptsModalBody',
        defaultMessage: 'Are you sure you want to delete the follow-up prompts of the selected pair(s)?'
    },
    importModalTitle: {
        id: 'qnaMaker.qnaMakerQnaList.importModalTitle',
        defaultMessage: 'Import Question Answer Pairs'
    },
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaList.searchPlaceholder',
        defaultMessage: 'Search pairs'
    },
    filtersApplied: {
        id: 'qnaMaker.qnaMakerQnaList.filtersApplied',
        defaultMessage: 'Filtered by:'
    },
    clearAll: {
        id: 'qnaMaker.qnaMakerQnaList.clearAll',
        defaultMessage: 'Clear all'
    },
    clearAllAriaLabel: {
        id: 'qnaMaker.qnaMakerQnaList.clearAllAriaLabel',
        defaultMessage: 'Clear all filters'
    }
});

export const QnaMakerQnaList = observer((props: QnaMakerQnaListProps) => {
    const { qnaUiStore, isCommandBarInExpandedState, setQnasCount, setMultiSelectMode } = props;
    const { rootStore, dispatcher } = useGlobalState();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const history = useHistory();

    const {
        qnaPairsJson,
        dirtyQnaPairs,
        displayedQnaPairStores,
        isAddQnaPairOn,
        filtersStores,
        isFilterBladeOpen,
        savingState,
        isTestPaneOpen,
        qnaPairsBeingEditedStore,
        deleteModalSettings,
        importProjectModalSettings,
        searchQuery
    } = qnaUiStore;

    const { resetMetricConfigsAfterSave, setTimestampToStartEditingOrAdding } = useQnaMetricActions();
    const { exportQnas, saveChanges, editQnaPair, deleteQnaPair, importQnas, addQnaPair } = useQnaPairsActions(qnaUiStore);

    const {
        qnaMetricStore,
        areQnaPairsLoaded,
        id: currentProjectId,
        sourcesStoreHouse: { items: sourceStores },
        qnasJsonData,
        isSmartSuggestionEnabled,
        haveSmartSuggestions
    } = rootStore.currentQnaMakerProjectStore;

    const isInEmptyState = areQnaPairsLoaded && !qnaPairsJson.length;

    const searchParams = new URLSearchParams(location.search);
    const decodedSourceFilterName = decodeURIComponent(searchParams.get('source'));

    const qnasFetchingTracker = useProgressTracker();

    const [isAddQnaPairModalHidden, setIsAddQnaPairModalHidden] = React.useState(true);

    const [selectedItems, setSelectedItems] = React.useState([]);

    React.useEffect(() => {
        if (savingState === 'dirty') {
            setTimestampToStartEditingOrAdding(Date.now());
        }
    }, [savingState]);

    React.useEffect(() => setMultiSelectMode(selectedItems.length > 1), [selectedItems]);

    React.useEffect(() => {
        if (qnaUiStore.selectedQnaId) {
            const focusedCard = document.getElementById(`qna${qnaUiStore.selectedQnaId}`);
            focusedCard?.focus();
        }
    }, [qnaUiStore.selectedQnaId]);

    const orderedQnas = React.useMemo(
        () =>
            substringSearchStrategy(qnaPairsJson, searchQuery, ['questions', 'answer'])
                .filter(
                    pair =>
                        applySourcesFilter(filtersStores.appliedSourcesFilters, pair) &&
                        applyMetadataFilter(filtersStores.appliedMetadataFilters, pair) &&
                        applyQuestionFilter(
                            filtersStores.appliedQuestionFilters,
                            pair,
                            qnaPairsBeingEditedStore.qnaPairsBeingEditedIds,
                            dirtyQnaPairs.map(p => p.value.id)
                        )
                )
                .sort((qna1, qna2) => {
                    if (qna1.source === 'Editorial') {
                        return qna1.id > qna2.id ? -1 : 1;
                    }

                    if (qna1.source !== qna2.source) {
                        return qna1.id > qna2.id ? -1 : 1;
                    }

                    return qna1.id > qna2.id ? 1 : -1;
                }),

        [
            searchQuery,
            qnaPairsJson,
            dirtyQnaPairs,
            filtersStores.appliedSourcesFilters,
            filtersStores.appliedMetadataFilters,
            filtersStores.appliedQuestionFilters,
            qnaPairsBeingEditedStore.qnaPairsBeingEditedIds
        ]
    );

    React.useEffect(() => setQnasCount(orderedQnas.length), [orderedQnas]);

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerSources', { tracker: qnasFetchingTracker });
    }, []);

    React.useEffect(() => {
        filtersStores.setSourcesFiltersItems(sourceStores);
    }, [sourceStores]);

    React.useEffect(() => {
        if (!Boolean(qnaUiStore.selectedQnaId) || orderedQnas.findIndex(p => p.id === qnaUiStore.selectedQnaId) === -1) {
            qnaUiStore.setSelectedQnaId(orderedQnas[0]?.id);
        }
    }, [orderedQnas]);

    React.useEffect(() => {
        if (sourceStores.length && decodedSourceFilterName && !filtersStores.appliedSourcesFilters.length) {
            filtersStores.applySourceFilterById(decodedSourceFilterName);
        }
    }, [sourceStores.length, qnasJsonData]);

    React.useEffect(() => {
        filtersStores.setMetadataFiltersItems(qnaPairsJson);
        qnaUiStore.setIsSmartSuggestionsMessageDisplayed(isSmartSuggestionEnabled && haveSmartSuggestions);

        if (!sourceStores.find(s => s.source === 'Editorial') && dirtyQnaPairs.some(d => d.op === 'add')) {
            dispatcher.dispatch('getQnaMakerSources', { tracker: qnasFetchingTracker, context: { forceRefresh: true } });
        }
    }, [qnaPairsJson]);

    const logCommandbarClick = (prop: string) => logQnaKBButtonClick(serviceName, 'QnACommandbar', 'Button', prop, currentProjectId);

    const logModalClick = (modalName: string, prop: string) =>
        logQnaKBButtonClick(serviceName, modalName, 'Button', prop, currentProjectId);

    const handleDiscardChanges = () => {
        qnaUiStore.resetQnaParis();
        resetMetricConfigsAfterSave();
    };

    const handleSaveChanges = () => {
        logButtonClickWithCustomData(serviceName, 'EditKb', 'SaveChanges', {
            isNewQna: true,
            projectId: currentProjectId,
            totalTimeToSave: Date.now() - qnaMetricStore.timestampToStartEditingOrAdding,
            totalNumberOfClicks: qnaMetricStore.totalNumberOfClicksBeforeSave + LuisConstants.QnaMaker.Metrics.saveClickCounter
        });

        resetMetricConfigsAfterSave();

        saveChanges(true);

        if (filtersStores.appliedQuestionFilters.find(f => f.id === QuestionsBeingEditedFilterItem)) {
            filtersStores.resetFilters('questions');
        }
    };

    const handleAddQna = () => {
        logCommandbarClick('AddQnA');
        qnaUiStore.setIsAddQnaPairOn(true);
        setIsAddQnaPairModalHidden(false);
    };

    const handleDismissAddQna = () => {
        logModalClick('AddQnAModal', 'Cancel');
        qnaUiStore.setIsAddQnaPairOn(false);
        setIsAddQnaPairModalHidden(true);
    };

    const handleTestQna = () => {
        logCommandbarClick('Test');
        qnaUiStore.setIsTestPaneOpen(!isTestPaneOpen);
    };

    const handleImport = (fileType: 'tsv' | 'xlsx') => {
        logCommandbarClick(fileType === 'xlsx' ? 'ImportExcel' : 'ImportTsv');
        qnaUiStore.setImportProjectModalSettings({ hidden: false, fileType });
    };

    const handleExport = (fileType: 'tsv' | 'xlsx') => {
        logCommandbarClick(fileType === 'xlsx' ? 'ExportExcel' : 'ExportTsv');
        exportQnas(fileType);
    };

    const handleDeleteQna = (operation: DeletionOperations) => {
        logCommandbarClick('Delete');
        qnaUiStore.setDeleteModalSettings({ hidden: false, operation });
    };

    const handleEditQnaPairFromTest = (qnaId: number) => {
        qnaUiStore.setIsTestPaneOpen(false);
        qnaUiStore.setSelectedQnaId(qnaId);
        qnaPairsBeingEditedStore.addPair(orderedQnas.find(q => q.id === qnaId));
    };

    const findLastIdx = () => {
        let maxIdx = -1;
        selectedItems.forEach(idx => {
            const tmpIdx = orderedQnas.findIndex(s => s.id === idx);
            if (tmpIdx > maxIdx) {
                maxIdx = tmpIdx;
            }
        });

        return maxIdx;
    };

    const handleSubmitDeleteQnaPair = () => {
        logModalClick(`Delete${deleteModalSettings.operation}Modal`, 'Confirm');
        const idx = findLastIdx();
        const newIdx = idx === orderedQnas.length - 1 ? 0 : idx + 1;
        deleteQnaPair(deleteModalSettings.operation, selectedItems);
        if (deleteModalSettings.operation === 'qna') {
            if (orderedQnas.length) {
                qnaUiStore.setSelectedQnaId(orderedQnas[newIdx].id);
            }
            setSelectedItems([]);
        }
    };

    const deleteModalRenderer = () => {
        let title;
        let body;
        switch (deleteModalSettings.operation) {
            case 'metadata':
                title = localize(messages.deleteQnaMetadataModalTitle);
                body = localize(messages.deleteQnaMetadataModalBody);
                break;
            case 'followUpPrompts':
                title = localize(messages.deleteQnaPromptsModalTitle);
                body = localize(messages.deleteQnaPromptsModalBody);
                break;
            default:
                title = localize(messages.deleteQnaPairModalTitle);
                body = localize(messages.deleteQnaPairModalBody, {
                    selectedCount: 1
                });
        }

        return (
            <DeletePromptModal
                dialogContentProps={{ title }}
                hidden={deleteModalSettings.hidden}
                onDelete={handleSubmitDeleteQnaPair}
                onDismiss={() => {
                    logModalClick(`Delete${deleteModalSettings.operation}Modal`, 'Cancel');
                    qnaUiStore.setDeleteModalSettings({ hidden: true, operation: null });
                }}>
                {body}
            </DeletePromptModal>
        );
    };

    const handleRemoveFilter = (filterType: QnaMakerFiltersType) => {
        if (filterType === 'sources' && decodedSourceFilterName) {
            history.push(`${routeTable.qnaMaker.editPage(currentProjectId)}?assets=qnas`);
        }
        filtersStores.resetFilters(filterType);
    };

    const handleOnResetFilters = () => {
        if (decodedSourceFilterName) {
            history.push(`${routeTable.qnaMaker.editPage(currentProjectId)}?assets=qnas`);
        }
        filtersStores.resetFilters();
    };

    const updateSelectedItems = (idx: number) => {
        const isSelected = selectedItems.includes(idx);
        if (isSelected) {
            setSelectedItems(selectedItems.filter(id => id !== idx));
        } else {
            setSelectedItems([...selectedItems, idx]);
        }
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 10 }}>
            <QnaMakerQnaPairCommandBar
                onExport={handleExport}
                isAddQnaPairOn={isAddQnaPairOn}
                onSave={handleSaveChanges}
                isInExpandedState={isCommandBarInExpandedState}
                isFilterBladeOpen={isFilterBladeOpen}
                isTestPaneOpen={isTestPaneOpen}
                isInDirtyState={savingState !== 'saved'}
                onDiscard={handleDiscardChanges}
                isDeleteDisabled={selectedItems.length === 0}
                onFilter={() => qnaUiStore.toggleIsFilterBladeOpen()}
                isInEmptyState={qnaUiStore.qnaPairsJson.length === 0}
                onAddNewQna={handleAddQna}
                onTest={handleTestQna}
                areEditActionsDisabled={qnaUiStore.savingState === 'inProgress'}
                onImport={handleImport}
                onDelete={handleDeleteQna}
            />
            <Stack horizontal tokens={{ padding: '10px' }} horizontalAlign="space-between">
                {!areQnaPairsLoaded ? (
                    <Spinner size={SpinnerSize.medium} />
                ) : (
                    <Checkbox
                        ariaLabel={localize(messages.checkbox)}
                        checked={selectedItems.length === orderedQnas.length}
                        onChange={(_, checked) => {
                            setSelectedItems([]);
                            if (checked) {
                                setSelectedItems(orderedQnas.map(qna => qna.id));
                            }
                        }}
                        styles={{ root: { alignSelf: 'center' } }}
                    />
                )}
                <LuisDebouncedCollapsableSearchBox
                    alwaysExpanded
                    autoFocus={false}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    size={isCommandBarInExpandedState ? 56 : 40}
                    placeholder={localize(messages.searchPlaceholder)}
                    value={searchQuery}
                    onChange={(_, newValue?: string) => qnaUiStore.setSearchQuery(newValue)}
                />
            </Stack>
            <ScrollablePane styles={{ root: { height: '100%', position: 'relative' } }}>
                {isInEmptyState ? (
                    <Stack styles={{ root: { width: '100%', paddingTop: '50px' } }}>
                        <LuisText styles={{ root: { textAlign: 'center', alignSelf: 'center', width: '80%' } }}>
                            {localize(messages.emptyStateMessage)}
                        </LuisText>
                    </Stack>
                ) : (
                    <>
                        {filtersStores.allAppliedFilters.length > 0 && (
                            <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5, padding: '0 0 0 5px' }}>
                                <QnaMakerFilterPillContainer
                                    filterItems={filtersStores.allAppliedFilters}
                                    onRemoveFilter={handleRemoveFilter}
                                />
                                <Link as="button" disabled={qnaUiStore.savingState === 'inProgress'} onClick={handleOnResetFilters}>
                                    <span aria-label={localize(messages.clearAllAriaLabel)}>{localize(messages.clearAll)}</span>
                                </Link>
                            </Stack>
                        )}
                        <AutoSizer>
                            {({ height, width }: { width: number; height: number }) => (
                                <FocusZone
                                    direction={FocusZoneDirection.vertical}
                                    defaultTabbableElement={`#qna${qnaUiStore.selectedQnaId}`}>
                                    <List height={height} width={width} itemSize={95} itemCount={orderedQnas.length}>
                                        {({ index, style }) => (
                                            <QnaMakerQnaCard
                                                style={style}
                                                checked={selectedItems.includes(orderedQnas[index].id)}
                                                isCheckboxVisible={selectedItems.length !== 0}
                                                isSelected={qnaUiStore.selectedQnaId === orderedQnas[index].id}
                                                toggleCheckbox={idx => updateSelectedItems(idx)}
                                                setIsSelected={() => qnaUiStore.setSelectedQnaId(orderedQnas[index].id)}
                                                qnaData={displayedQnaPairStores.find(q => q.id === orderedQnas[index].id)}
                                            />
                                        )}
                                    </List>
                                </FocusZone>
                            )}
                        </AutoSizer>
                    </>
                )}
            </ScrollablePane>

            <QnaMakerTestPane
                isOpen={qnaUiStore.isTestPaneOpen}
                onEditQnA={handleEditQnaPairFromTest}
                onDismiss={() => qnaUiStore.setIsTestPaneOpen(false)}
                onAddQuestion={(answerId, questions) => {
                    editQnaPair(answerId, { questions });
                }}
                flattenMetadata={getFlattenMetadata(qnaPairsJson)}
            />

            {deleteModalRenderer()}

            <ImportModal
                isNameFieldDisplayed={false}
                hidden={importProjectModalSettings.hidden}
                fileType={importProjectModalSettings.fileType}
                dialogContentProps={{ title: localize(messages.importModalTitle) }}
                onDismiss={() => qnaUiStore.setImportProjectModalSettings({ hidden: true })}
                nameValidators={[FormValidators.createBlacklistValidator({ characterList: `` })]}
                onSubmit={data => {
                    importQnas({ ...data, fileType: importProjectModalSettings.fileType });
                    qnaUiStore.setImportProjectModalSettings({ hidden: true });
                }}
            />

            {isFilterBladeOpen && (
                <QnaMakerFiltersBlade
                    isOpen={isFilterBladeOpen}
                    sourcesFilters={filtersStores.sourcesFilters}
                    metadataFilters={filtersStores.metadataFilters}
                    questionsFilters={filtersStores.questionsFilters}
                    suggestionFilters={filtersStores.suggestionFilters}
                    onDismiss={() => qnaUiStore.toggleIsFilterBladeOpen()}
                    onFiltersChange={filters => {
                        filtersStores.setFilterItems(filters);
                    }}
                />
            )}

            <QnaMakerAddQnaPairModal
                sources={sourceStores}
                qnaPairsJson={qnaPairsJson}
                onDismiss={handleDismissAddQna}
                hidden={isAddQnaPairModalHidden}
                onEditQnaPair={(data, id) => editQnaPair(id, data)}
                onSubmitQnaPair={data => {
                    logModalClick('AddQnAModal', 'Confirm');
                    addQnaPair(data);
                    qnaUiStore.setSelectedQnaId(data.id);
                    handleDismissAddQna();
                }}
                selectedSourceId={filtersStores.appliedSourcesFilters.length === 1 ? filtersStores.appliedSourcesFilters[0].id : null}
            />
        </Stack>
    );
});
